import { Injectable } from "@angular/core";
import { map, orderBy, sortBy, uniqBy } from "lodash";
import { ITask } from "src/modules/authentication/interfaces/task.interface";
import { HomecarePatient } from "src/modules/shared/interfaces/homecare-patient.interface";
import { ICarePlan } from "src/modules/shared/interfaces/care-plan.interface";
import { IUser } from "src/modules/authentication/interfaces/user.interface";
import { SkillsService } from "src/modules/shared/services/skills.service";
import { UserService } from "src/modules/shared/services/user.service";
import { IDefaultRate } from "../interfaces/default-rate.interface";
import { CompanyDashboardService } from "./company-dashboard.service";
import { ITeam } from "../interfaces/team.interface";
import { SkillInterface } from "src/modules/shared/interfaces/skill.interface";
import { BroadcastJobsUtilService } from "./broadcast-job-utils.service";
import { IUserAddress } from "src/modules/authentication/interfaces/user-address.interface";
import { RATE_STEP, getRateOptionsStart } from "src/modules/shared/utils/rate-options.util";

// The care plans the task picker offers for one patient
export interface ICarePlanChoices {
    assigned: ICarePlan[];
    others: ICarePlan[];
    // The plan to fill in when the patient is picked
    preselected: ICarePlan;
}

@Injectable({
    providedIn: 'root'
})

export class BroadcastCommonMethodsService {

    constructor(
        private userService: UserService,
        private skillsService: SkillsService,
        private companyDashboardService: CompanyDashboardService,
        private broadcastJobsUtilService: BroadcastJobsUtilService
        
    ) {

    }

    
     //Skill Method
     async fetchSkillsAndCities(companyType, secondaryCompanyTypes): Promise<[string[], string]> {
        try {
          let industries = await this.skillsService.getListOfAllSkills({ outputFormat: 'names', industriesToInclude: [companyType, ...secondaryCompanyTypes] }) as string[]; 
          industries = orderBy(industries);
          const initialSkill = industries[0].toString();
          return [industries, initialSkill];
        } catch (error) {
          console.log(error);
        }
    
      }

    //Default Rates Methods 
    async fetchSkillsWithRates(entityRates, companyType, secondaryCompanyTypes): Promise<IDefaultRate> {
        try {
        //fetch default skill rates from skill.json
        const defaultRates = await this.skillsService.getListOfAllSkills({ outputFormat: 'manual_rates', industriesToInclude: [companyType, ...secondaryCompanyTypes]}) as {rate: number; allowChange: boolean, defaultRate: number};
        // combine both rates
        for(let skill in defaultRates){
            // if rate is set for the skill in entity profile
            if(entityRates && entityRates[skill]){
            defaultRates[skill]['rate'] = entityRates[skill].rate,
            defaultRates[skill]['allowChange'] = entityRates[skill].allowChange;
            defaultRates[skill]['minPrice'] = entityRates[skill].minPrice || entityRates[skill].rate || 20;
            defaultRates[skill]['maxPrice'] = entityRates[skill].maxPrice || 100;
          }
        }
        return defaultRates;
        } catch (error) {
        console.log(error);
        }
    }

    getRates(defaultRates: IDefaultRate, selectedSkill: string, userData: IUser): [Array<{id: string, label: string}>, boolean, string] {
        let listOfRates = [];

        let rate: string;

        // minimum rate is the default rate set in skills.json
        const stateDefaultRates = defaultRates[selectedSkill]['defaultRate'] || 20;
        // if entity rate is undefined or equal to default rate, minium rate would be selected for the selected skill
        
        // Min/Max price of skill
        const minPrice = defaultRates[selectedSkill]['minPrice'] || stateDefaultRates;
        const maxPrice = defaultRates[selectedSkill]['maxPrice'] || 100;
        // if entity rate is undefined or equal to default rate, minium rate would be selected for the selected skill
        let rateIndex = -1;
        // index of the previous minimum rate, which is the default selection
        let minIndex = 0;
        for (let i = getRateOptionsStart(minPrice); i <= maxPrice; i += RATE_STEP) {

          if(defaultRates[selectedSkill]['rate'] < i && defaultRates[selectedSkill]['rate'] > (i-0.5)){
        
            listOfRates.push({
            id: defaultRates[selectedSkill]['rate'],
            label: '$' + defaultRates[selectedSkill]['rate'].toFixed(2)
            });
            
            rateIndex = listOfRates.length - 1;
        }

        listOfRates.push({
            id: i,
            label: '$' + i.toFixed(2)
        });

        if (i === minPrice) {
            minIndex = listOfRates.length - 1;
        }

        // if entity rates are not equal to default rates then we need the entity rates to be selected by default
        if (i === defaultRates[selectedSkill]['rate']){
            rateIndex = listOfRates.length - 1;
        }


    }

    if (rateIndex < 0) {
        rateIndex = minIndex;
    }

      const ENTITY_ID = "69497ff5bdd97e1bcf01d8a5";

      const rateMap = {
        'registered practical nurse': [33.25, 36.25, 40.75],
        'registered nurse': [43.25, 46.25, 50.75],
        'registered psychiatric nurse': [54.25, 57.25, 61.75]
      };

      const isTargetEntity =
        userData?.entityId?._id === ENTITY_ID;

      const rates = rateMap[selectedSkill];

      if (isTargetEntity && rates) {
        listOfRates = rates.map(rate => ({
          id: rate,
          label: `$${rate}`
        }));

        rateIndex = 0;
      }

        
        if (selectedSkill === 'catering bartender' || selectedSkill === 'catering server' ||
        selectedSkill === 'event bartender' || selectedSkill === 'event server') {
        rate = listOfRates[minIndex + 5].id;
        } else {
        rate = listOfRates[rateIndex].id;
        }

        const allowChange = defaultRates[selectedSkill]['allowChange'] !== undefined ? defaultRates[selectedSkill]['allowChange'] : true;
        return [listOfRates, allowChange, rate.toString()];
    }

    async fetchExpectedRates(allAddressesOfCompany: IUserAddress[], selectedSkill: string): Promise<number> {
      
      const selectedCompanyAddress =  this.getDefaultCompanyAddress(allAddressesOfCompany)
      
      
      try{
        const latitude =  selectedCompanyAddress.latitude;
        const longitude = selectedCompanyAddress.longitude;
        const result = await this.broadcastJobsUtilService.getExpectedRates(selectedSkill, { latitude, longitude});
        return result.averageRate;
      } catch(error) {
        console.log(error);
      }
    }
  
    
    //Sub Skill Methods
    async fetchSubSkills(): Promise<any> {
      let subSkills = [];
      try {
        subSkills = await this.userService.getSubSkills();
        subSkills = sortBy(map(subSkills, 'name'));
          return subSkills;
      } catch (error) {
        console.log(error);
      }
    }

    async setDefaultSubSkills(requiredTrainingSubSkill, initialSkill): Promise<string[]> {

      if(!requiredTrainingSubSkill) {
        return [];
      }
      return await this.broadcastJobsUtilService.requiredTrainingSubSkill(requiredTrainingSubSkill, initialSkill, []);  
    }


    //Credit Card Methods
    async checkForCreditCard(userData: IUser): Promise<boolean> {
        const responseFromWhiteList = userData.type === 'employer' && userData.email ? await this.hasCCRequiredForTheShift(userData) : null;
        return !(userData.customer.hasCreditCard || userData.customer.status || responseFromWhiteList)
    }

    async hasCCRequiredForTheShift(userData?: IUser): Promise<boolean> {
      let checkPaymentInfoStatusFromAdmin; 
      try {
        checkPaymentInfoStatusFromAdmin = await this.userService.isUserExistInWhiteList(userData.email, userData._id);
      } catch (error) {
        checkPaymentInfoStatusFromAdmin = false;
      } finally {
        return checkPaymentInfoStatusFromAdmin;
      }
    }
    

    //Team Methods 
    async getTeams(entityId): Promise<ITeam[]> {
      try {
        return await this.companyDashboardService.getTeams(entityId);
      } catch (error) {
        console.log(error);
      }
    }

    //Address
    getDefaultCompanyAddress(allAddressesOfCompany): IUserAddress {
      let selectedCompanyAddress = allAddressesOfCompany[0];
      allAddressesOfCompany.forEach(add => {
        if (add.default && add.street) {
          selectedCompanyAddress = add;
        }
      });
      return selectedCompanyAddress;
    }

    //Timings method
    
  getStartandEndTimeForDay(startVal: string, endVal: string): [Array<any>, Array<any>] {
    let startTimeValues = this.broadcastJobsUtilService.generatedStepArray(startVal);
    let endTimeValues = this.broadcastJobsUtilService.generatedStepArray(endVal);
    startTimeValues = startTimeValues.filter(item => item.formatted !== 'Next Day') 
    endTimeValues = endTimeValues.filter(item => item.formatted !== 'Next Day') 
    
    return [startTimeValues, endTimeValues]
  }  
  
    //Home care methods
    assignHomeCareProperties(userData: IUser): Array<any> {
      const isHomeCareClient = true;
      const availableTasks = [...userData.entityId.homecareClient.tasks];
      const availablePatients = [...userData.entityId.homecareClient.patients];
    
      return [isHomeCareClient, availableTasks, availablePatients];

    }

    // The address field of the patient picker emits only {id, address}, so the stored
    // patient, which has the _id care plans are assigned by, is looked up by its id.
    findStoredPatient(patients: HomecarePatient[], patientId: string): HomecarePatient {
      return (patients || []).find(patient => patient.id === patientId);
    }

    // Splits the company's care plans for the task picker of one patient: the ones
    // assigned to that patient, and the rest, which can still be picked for them.
    // A plan saved from another patient's shift is left out of the rest.
    // The plan to fill in is the one used on the patient's last shift, as long as it
    // still exists; otherwise the first one assigned to them.
    getCarePlanChoices(carePlans: ICarePlan[], patient: HomecarePatient): ICarePlanChoices {
      const assigned = [];
      const others = [];

      (carePlans || []).forEach(carePlan => {
        if (patient?._id && (carePlan.patients || []).includes(patient._id)) {
          assigned.push(carePlan);
        } else if (!carePlan.savedFromShift) {
          others.push(carePlan);
        }
      });

      const lastUsed = [...assigned, ...others].find(carePlan => carePlan._id === patient?.lastCarePlanId);

      return { assigned, others, preselected: lastUsed || assigned[0] };
    }

    // Turns a care plan into the tasks of a shift. A task picked from the company's
    // task list becomes that task, so it shows its current wording and the task
    // picker stops offering it. A custom task, or one whose company task was since
    // deleted, uses the text saved on the plan.
    getCarePlanTasks(carePlan: ICarePlan, availableTasks: ITask[]): ITask[] {
      if (!carePlan?.tasks?.length) {
        return [];
      }

      const tasks = carePlan.tasks.map(planTask => {
        const companyTask = planTask.taskId && (availableTasks || []).find(task => task._id === planTask.taskId);

        return companyTask || { _id: planTask._id, title: planTask.title, description: planTask.description };
      });

      return uniqBy(tasks, '_id');
    }

}